import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, tap } from 'rxjs';
import { DataService } from './data.service';
import { getEventLandscapeImageUrl } from '../utils/event-image.util';
import { formatEventDateTimeLineFromRecord } from '../utils/date-parse.util';

type WishlistRecord = Record<string, unknown>;

export interface WishlistItem {
  id: string;
  referenceNo: string;
  title: string;
  venue: string;
  city: string;
  dateLabel: string;
  category: string;
  categoryTone: 'devotional' | 'concert' | 'conference';
  priceLabel: string;
  badgeLabel: string;
  imageUrl: string;
  expiresSoon?: boolean;
  raw: WishlistRecord;
}

@Injectable({
  providedIn: 'root'
})
export class WishlistService {
  private readonly wishlistRefsSubject = new BehaviorSubject<Set<string>>(new Set<string>());
  readonly wishlistRefs$ = this.wishlistRefsSubject.asObservable();

  constructor(private dataService: DataService) {}

  addToWishlist(referenceNo: string): Observable<unknown> {
    const normalized = this.normalizeReferenceNo(referenceNo);
    return this.dataService.post('wishlist', { reference_no: normalized }).pipe(
      tap((response) => {
        // console.log('WISHLIST CREATE RESPONSE', response);
        this.addRef(normalized);
      })
    );
  }

  removeFromWishlist(referenceNo: string): Observable<unknown> {
    const normalized = this.normalizeReferenceNo(referenceNo);
    return this.dataService.delete(`whishlist/${encodeURIComponent(normalized)}`).pipe(
      tap((response) => {
        // console.log('WISHLIST DELETE RESPONSE', response);
        this.removeRef(normalized);
      })
    );
  }

  getWishlist(limit: number = 20, page: number = 1): Observable<WishlistItem[]> {
    return this.dataService.get('wishlist', { limit, page }).pipe(
      tap((response) => {
        // console.log('WISHLIST GET RESPONSE', response);
      }),
      map((response) => this.normalizeWishlist(response))
    );
  }

  syncWishlistRefs(limit: number = 100, page: number = 1): Observable<WishlistItem[]> {
    return this.getWishlist(limit, page).pipe(
      tap((items) => {
        this.setRefs(items.map((item) => item.referenceNo));
      })
    );
  }

  clearWishlistRefs(): void {
    this.wishlistRefsSubject.next(new Set<string>());
  }

  isWishlisted(referenceNo: string | null | undefined): boolean {
    const normalized = this.normalizeReferenceNo(referenceNo);
    if (!normalized) {
      return false;
    }
    return this.wishlistRefsSubject.value.has(normalized);
  }

  extractReferenceNo(record: Record<string, unknown> | null | undefined): string | null {
    if (!record) {
      return null;
    }
    return this.extractReferenceNoDeep(record);
  }

  private normalizeWishlist(response: unknown): WishlistItem[] {
    return this.extractRecords(response)
      .map((record, index) => this.normalizeWishlistItem(record, index))
      .filter((item): item is WishlistItem => item !== null);
  }

  private normalizeWishlistItem(record: WishlistRecord, index: number): WishlistItem | null {
    const eventRecord = this.resolveEventRecord(record);
    const referenceNo = this.extractReferenceNo(eventRecord) ?? this.extractReferenceNo(record);
    if (!referenceNo) {
      return null;
    }

    const title =
      this.pickString(eventRecord, ['title', 'name', 'event_name', 'event_title']) ||
      this.pickString(record, ['title', 'name', 'event_name', 'event_title']) ||
      'Untitled Event';
    const venue =
      this.pickString(eventRecord, ['venue', 'venue_name', 'place', 'location_name']) ||
      this.pickString(record, ['venue', 'venue_name', 'place', 'location_name']) ||
      '';
    const city =
      this.pickString(eventRecord, ['city', 'city_name', 'location']) ||
      this.pickString(record, ['city', 'city_name', 'location']) ||
      '';
    const category = this.resolveCategory(eventRecord, record);
    const dateLabel =
      formatEventDateTimeLineFromRecord(eventRecord) ||
      formatEventDateTimeLineFromRecord(record) ||
      'Date TBA';

    return {
      id: `wishlist-${referenceNo}-${index}`,
      referenceNo,
      title,
      venue,
      city,
      dateLabel,
      category,
      categoryTone: this.resolveCategoryTone(category),
      priceLabel: this.resolvePriceLabel(eventRecord, record),
      badgeLabel: category || 'Event',
      imageUrl: getEventLandscapeImageUrl(eventRecord) || getEventLandscapeImageUrl(record) || '',
      expiresSoon: this.isExpiringSoon(eventRecord, record),
      raw: {
        ...record,
        ...eventRecord,
        reference_no: referenceNo,
        id: eventRecord['id'] ?? record['id'] ?? referenceNo
      }
    };
  }

  private extractRecords(payload: unknown): WishlistRecord[] {
    const queue: unknown[] = [payload];
    const visited = new Set<unknown>();

    while (queue.length > 0) {
      const current = queue.shift();
      if (current == null || visited.has(current)) {
        continue;
      }
      visited.add(current);

      if (Array.isArray(current)) {
        return current
          .filter((entry) => entry != null && typeof entry === 'object' && !Array.isArray(entry))
          .map((entry) => entry as WishlistRecord);
      }

      if (typeof current !== 'object') {
        continue;
      }

      const record = current as WishlistRecord;
      for (const key of ['wishlist', 'wishlists', 'data', 'result', 'items', 'rows', 'list']) {
        const value = record[key];
        if (Array.isArray(value)) {
          return value
            .filter((entry) => entry != null && typeof entry === 'object' && !Array.isArray(entry))
            .map((entry) => entry as WishlistRecord);
        }
        if (value && typeof value === 'object') {
          queue.push(value);
        }
      }

      for (const value of Object.values(record)) {
        if (value && typeof value === 'object') {
          queue.push(value);
        }
      }
    }

    return [];
  }

  private pickString(record: WishlistRecord, keys: string[]): string | null {
    for (const key of keys) {
      const value = record[key];
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed) {
          return trimmed;
        }
      }
    }
    return null;
  }

  private resolveEventRecord(record: WishlistRecord): WishlistRecord {
    const candidates = ['event', 'event_details', 'eventDetail', 'details', 'data', 'item'];
    for (const key of candidates) {
      const value = record[key];
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        return {
          ...record,
          ...(value as WishlistRecord)
        };
      }
    }
    return record;
  }

  private resolveCategory(primary: WishlistRecord, fallback?: WishlistRecord): string {
    const categories = primary['categories'];
    if (Array.isArray(categories)) {
      const first = categories.find((entry) => typeof entry === 'string' && entry.trim());
      if (typeof first === 'string') {
        return first.trim();
      }
    }

    return (
      this.pickString(primary, ['category', 'event_type', 'type', 'genre']) ||
      this.pickString(fallback ?? {}, ['category', 'event_type', 'type', 'genre']) ||
      'Event'
    );
  }

  private resolveCategoryTone(category: string): 'devotional' | 'concert' | 'conference' {
    const normalized = category.trim().toLowerCase();
    if (normalized.includes('concert') || normalized.includes('music')) {
      return 'concert';
    }
    if (normalized.includes('conference') || normalized.includes('expo')) {
      return 'conference';
    }
    return 'devotional';
  }

  private resolvePriceLabel(primary: WishlistRecord, fallback?: WishlistRecord): string {
    const pricing = primary['pricing'] ?? fallback?.['pricing'];
    const currency =
      this.pickString(primary, ['currency']) ||
      this.pickString(fallback ?? {}, ['currency']) ||
      'Rs';

    if (pricing && typeof pricing === 'object' && !Array.isArray(pricing)) {
      const min = (pricing as WishlistRecord)['min_price'];
      if (min != null && String(min).trim() !== '') {
        return `${currency} ${String(min).trim()} onwards`;
      }
    }

    const minPrice = primary['min_price'] ?? fallback?.['min_price'];
    if (minPrice != null && String(minPrice).trim() !== '') {
      return `${currency} ${String(minPrice).trim()} onwards`;
    }

    if (typeof pricing === 'number' || typeof pricing === 'string') {
      const raw = String(pricing).trim();
      if (!raw) {
        return 'Price TBA';
      }
      if (raw === '0' || raw.toLowerCase() === 'free') {
        return 'FREE';
      }
      return `${currency} ${raw}`;
    }

    return 'Price TBA';
  }

  private isExpiringSoon(primary: WishlistRecord, fallback?: WishlistRecord): boolean {
    const raw =
      this.pickString(primary, [
        'start_date',
        'date',
        'event_date',
        'show_date',
        'event_start_date',
        'eventDate',
        'startDate'
      ]) ||
      this.pickString(fallback ?? {}, [
        'start_date',
        'date',
        'event_date',
        'show_date',
        'event_start_date',
        'eventDate',
        'startDate'
      ]) ||
      '';
    if (!raw) {
      return false;
    }

    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) {
      return false;
    }

    const diff = date.getTime() - Date.now();
    const days = diff / (1000 * 60 * 60 * 24);
    return days >= 0 && days <= 7;
  }

  private normalizeReferenceNo(value: unknown): string {
    return String(value ?? '').trim();
  }

  private extractReferenceNoDeep(value: unknown, depth: number = 0): string | null {
    if (depth > 3 || value == null) {
      return null;
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const normalized = this.normalizeReferenceNo(value);
      return normalized || null;
    }

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.extractReferenceNoDeep(item, depth + 1);
        if (found) {
          return found;
        }
      }
      return null;
    }

    if (typeof value !== 'object') {
      return null;
    }

    const record = value as WishlistRecord;
    const preferredKeys = [
      'reference_no',
      'referenceNo',
      'event_reference_no',
      'eventReferenceNo',
      'event_code',
      'eventCode'
    ];
    const fallbackKeys = [
      'event_id',
      'eventId',
      'id'
    ];

    for (const key of preferredKeys) {
      const normalized = this.normalizeReferenceNo(record[key]);
      if (normalized) {
        return normalized;
      }
    }

    for (const key of ['event', 'event_details', 'eventDetail', 'details', 'data', 'item']) {
      const found = this.extractReferenceNoDeep(record[key], depth + 1);
      if (found) {
        return found;
      }
    }

    for (const key of fallbackKeys) {
      const normalized = this.normalizeReferenceNo(record[key]);
      if (normalized) {
        return normalized;
      }
    }

    return null;
  }

  private addRef(referenceNo: string): void {
    const next = new Set(this.wishlistRefsSubject.value);
    next.add(referenceNo);
    this.wishlistRefsSubject.next(next);
  }

  private removeRef(referenceNo: string): void {
    const next = new Set(this.wishlistRefsSubject.value);
    next.delete(referenceNo);
    this.wishlistRefsSubject.next(next);
  }

  private setRefs(referenceNos: string[]): void {
    this.wishlistRefsSubject.next(
      new Set(referenceNos.map((value) => this.normalizeReferenceNo(value)).filter(Boolean))
    );
  }
}
